// useFcm.ts
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { useUser } from 'hooks';
import { useAsync } from 'hooks/useAsync';
import { FcmTokenAtom } from 'modules/fcm';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSetRecoilState } from 'recoil';

const audioCtx = new AudioContext();

function unlockAudio() {
  if (audioCtx.state === 'suspended') {
    audioCtx.resume().catch(console.error);
  }
  document.removeEventListener('click', unlockAudio);
  document.removeEventListener('touchstart', unlockAudio);
}
document.addEventListener('click', unlockAudio);
document.addEventListener('touchstart', unlockAudio);

const FIREBASE_CONFIG = {
  apiKey: import.meta.env.VITE_FCM_API_KEY,
  authDomain: import.meta.env.VITE_FCM_DOMAIN,
  projectId: import.meta.env.VITE_FCM_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FCM_BUCKET,
  messagingSenderId: import.meta.env.VITE_FCM_SENDER_ID,
  appId: import.meta.env.VITE_FCM_APP_ID,
  measurementId: import.meta.env.VITE_FCM_MEAS_ID
};
//console.log(12121, import.meta.env);
const VAPID_KEY = import.meta.env.VITE_FCM_KEY;
const SERVICE_WORKER_PATH = '/firebase-messaging-sw.js';

export const useFcm = () => {
  const { isLogin } = useUser();
  const { showAlarm, closeAlarm } = useAsync();
  const setFcmToken = useSetRecoilState(FcmTokenAtom);

  const isAlarmDeniedShownRef = useRef(false);
  const app = useRef(initializeApp(FIREBASE_CONFIG));
  const messaging = useRef(getMessaging(app.current));
  const sosAudioRef = useRef<HTMLAudioElement>(new Audio('/assets/audio/ping.mp3'));

  // 알림 권한 변경될 경우 재요청을 위한 상태
  const [notificationPermission, setNotificationPermission] = useState(Notification.permission);

  const requestTokenAndRegister = useCallback(
    async (registration: ServiceWorkerRegistration) => {
      try {
        let currentPermission = Notification.permission;

        if (currentPermission === 'default') {
          currentPermission = await Notification.requestPermission();
        }

        setNotificationPermission(currentPermission);

        if (currentPermission === 'granted') {
          const token = await getToken(messaging.current, {
            vapidKey: VAPID_KEY,
            serviceWorkerRegistration: registration
          });
          console.log('FCM Token:', token);
          setFcmToken(token);
          isAlarmDeniedShownRef.current = false;
        }

        if (currentPermission === 'denied') {
          console.warn('FCM 토큰을 가져올 수 없습니다. 브라우저 알림이 차단되었습니다.');

          if (!isAlarmDeniedShownRef.current) {
            showAlarm({
              modalTitle: 'Notification Permission Denied',
              message:
                'You must allow notification permission in your browser settings to receive FCM notifications. (Please check the lock icon in the address bar.)',
              noFooter: false,
              isSingleFooter: true,
              closeButtonProps: {
                onClick: closeAlarm
              }
            });
            isAlarmDeniedShownRef.current = true;
          }
        }
      } catch (error) {
        console.error('FCM Registration or Token Error:', error);
      }
    },
    [setFcmToken, showAlarm, closeAlarm]
  );

  useEffect(() => {
    if (!isLogin) return;

    // 1) Firebase / Service Worker 등록
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register(SERVICE_WORKER_PATH)
        .then(requestTokenAndRegister)
        .catch(console.error);
    }

    // 2) Foreground 메시지 수신 → 브라우저 Notification
    const unsubscribe = onMessage(messaging.current, (payload) => {
      console.log('포그라운드 메시지 수신:', payload);
      const { title = '', ...rest } = payload.notification ?? {};

      if (sosAudioRef.current && title === 'SOS') {
        sosAudioRef.current.pause();
        sosAudioRef.current.currentTime = 0;
        sosAudioRef.current.play().catch((error) => {
          console.error('Audio play error:', error);
        });
      }

      showAlarm({
        modalTitle: 'SOS',
        message: rest.body,
        noFooter: false,
        isSingleFooter: true,
        closeButtonProps: {
          onClick: () => {
            closeAlarm();
          }
        }
      });
    });

    return () => unsubscribe();
  }, [isLogin, showAlarm, closeAlarm, setFcmToken, requestTokenAndRegister]);

  // 알림 권한 denied -> granted로 바뀐 경우 재요청
  useEffect(() => {
    if (!isLogin) return;

    const handleFocus = async () => {
      const newPermission = Notification.permission;

      // 이전 상태는 'granted'가 아니었으나, 현재 상태가 'granted'로 변경된 경우에만 재요청
      if (newPermission === 'granted' && notificationPermission !== 'granted') {
        console.log('Permission changed to granted. Re-attempting FCM registration...');

        // Service Worker 등록(또는 기존 등록 가져오기) 후 토큰 요청
        if ('serviceWorker' in navigator) {
          try {
            const registration =
              (await navigator.serviceWorker.getRegistration(SERVICE_WORKER_PATH)) ||
              (await navigator.serviceWorker.register(SERVICE_WORKER_PATH));
            requestTokenAndRegister(registration);
          } catch (error) {
            console.error('Service Worker registration on focus failed:', error);
          }
        }
      }

      if (newPermission !== notificationPermission) {
        setNotificationPermission(newPermission);
      }
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [isLogin, notificationPermission, requestTokenAndRegister]);
};
